import React from 'react';
import { useNavigate } from 'react-router-dom';

interface TrialExpiredModalProps {
  userRole: 'leader' | 'member' | string;
  leaders: Array<{ id: number; name: string; email: string }>;
  onLogout: () => void;
}

/* 旧 styled-components 定義をクラス文字列定数として保持。
 * 旧 keyframes `fadeIn` は globals.css の既存 `em-fade-in` と同内容なので再利用、
 * 旧 keyframes `slideUp` は `msc-slide-up-20` として globals.css に移設。
 * `PlanFeatureItem::before` の data URI マスクだけは
 * `.msc-plan-feature-check` として globals.css に置いてある。 */
const OVERLAY =
  'fixed top-0 left-0 right-0 bottom-0 bg-black/[0.65] backdrop-blur-[8px] ' +
  'flex items-center justify-center z-[2000] p-5 animate-[em-fade-in_0.3s_ease-out]';

const MODAL_CONTAINER =
  'bg-white rounded-tasky-lg w-full max-w-[460px] px-8 py-10 ' +
  'shadow-[0_20px_60px_rgba(0,0,0,0.3)] animate-[msc-slide-up-20_0.4s_ease-out] text-center';

const ICON_CIRCLE =
  'w-14 h-14 rounded-full bg-[linear-gradient(135deg,#fef3c7,#fde68a)] ' +
  'flex items-center justify-center mt-0 mx-auto mb-5 text-[1.5rem]';

const TITLE = 'text-[1.375rem] font-bold text-gray-800 m-0 mb-3'; /* #1f2937 */

const DESCRIPTION = 'text-[0.9375rem] text-gray-500 m-0 mb-6 leading-[1.6]'; /* #6b7280 */

const PLAN_CARD = 'border-2 border-violet-600 rounded-tasky-md p-6 mb-6 relative'; /* #7c3aed */

const PLAN_BADGE =
  'absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-600 text-white text-[0.6875rem] ' +
  'font-bold px-4 py-1 rounded-tasky-xl whitespace-nowrap';

const PLAN_NAME = 'text-[1rem] font-semibold text-gray-700 mb-1 mt-2'; /* #374151 */

const PLAN_PRICE_ROW = 'flex items-baseline justify-center gap-1 mb-3';

const PLAN_PRICE_SUFFIX = 'text-[0.875rem] text-gray-500';

const PLAN_FEATURES = 'list-none p-0 m-0 text-left';

const PLAN_FEATURE_ITEM =
  'msc-plan-feature-check flex items-center gap-2 text-[0.8125rem] text-gray-700 px-0 py-1';

const PRIMARY_BUTTON =
  'w-full px-6 py-3.5 text-[1rem] font-semibold text-white bg-violet-600 border-0 ' +
  'rounded-tasky-sm cursor-pointer transition-all duration-200 ease-[ease] mb-3 ' +
  'hover:bg-violet-700 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(124,58,237,0.3)]'; /* #6d28d9 */

const DIVIDER = 'border-t border-t-gray-200 mt-6 mx-0 mb-4'; /* #e5e7eb */

const FOOTER_LINKS = 'flex justify-center gap-6';

const FOOTER_LINK =
  'bg-transparent border-0 text-gray-400 text-[0.8125rem] cursor-pointer p-1 ' +
  'transition-[color] duration-200 ease-[ease] hover:text-gray-500 hover:underline'; /* #9ca3af / #6b7280 */

const LEADER_INFO =
  'bg-[linear-gradient(135deg,#f0f9ff_0%,#e0f2fe_100%)] rounded-tasky-md px-6 py-5 mb-6 text-left';

const LEADER_NAME = 'text-[0.9375rem] font-semibold text-blue-800 mb-1'; /* #1e40af */

const LEADER_EMAIL = 'text-[0.8125rem] text-blue-500'; /* #3b82f6 */

const TrialExpiredModal: React.FC<TrialExpiredModalProps> = ({
  userRole,
  leaders,
  onLogout,
}) => {
  const navigate = useNavigate();
  const isLeader = userRole === 'leader';

  const handleViewPlans = () => {
    navigate('/plans');
  };

  if (!isLeader) {
    return (
      <div className={OVERLAY}>
        <div className={MODAL_CONTAINER}>
          <div className={ICON_CIRCLE}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V13M12 17H12.01M12 3L2 20H22L12 3Z" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className={TITLE}>トライアル期間が終了しました</h2>
          <p className={DESCRIPTION}>
            ご利用を続けるには、組織のリーダーにお問い合わせください。
          </p>

          {leaders.length > 0 && (
            <div className={LEADER_INFO}>
              {leaders.map((leader) => (
                <div key={leader.id} style={{ marginBottom: leaders.length > 1 ? '8px' : 0 }}>
                  <div className={LEADER_NAME}>{leader.name}</div>
                  <div className={LEADER_EMAIL}>{leader.email}</div>
                </div>
              ))}
            </div>
          )}

          <hr className={DIVIDER} />
          <div className={FOOTER_LINKS}>
            <button className={FOOTER_LINK} onClick={onLogout}>ログアウト</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={OVERLAY}>
      <div className={MODAL_CONTAINER}>
        <div className={ICON_CIRCLE}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9V13M12 17H12.01M12 3L2 20H22L12 3Z" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h2 className={TITLE}>トライアル期間が終了しました</h2>
        <p className={DESCRIPTION}>
          引き続きTaskyをご利用いただくには、有料プランへの移行をお願いします。
        </p>

        <div className={PLAN_CARD}>
          <div className={PLAN_BADGE}>Starter / Pro / Enterprise</div>
          <div className={PLAN_NAME}>ご利用状況に合ったプランを選択</div>
          <div className={PLAN_PRICE_ROW}>
            <span className={PLAN_PRICE_SUFFIX}>月額 9,800円〜</span>
          </div>
          <ul className={PLAN_FEATURES}>
            <li className={PLAN_FEATURE_ITEM}>バナー無制限生成（Pro以上）</li>
            <li className={PLAN_FEATURE_ITEM}>高解像度ダウンロード</li>
            <li className={PLAN_FEATURE_ITEM}>全スタイル利用可能</li>
            <li className={PLAN_FEATURE_ITEM}>AIチャットサポート</li>
          </ul>
        </div>

        <button className={PRIMARY_BUTTON} onClick={handleViewPlans}>
          プランを選んで申し込む
        </button>

        <hr className={DIVIDER} />
        <div className={FOOTER_LINKS}>
          <button className={FOOTER_LINK} onClick={() => { window.location.href = '/organization'; }}>
            組織設定
          </button>
          <button className={FOOTER_LINK} onClick={onLogout}>ログアウト</button>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiredModal;
